import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { ScrollArea } from "../components/ui/scroll-area";
import AdminSidebar from "../components/layout/AdminSidebar";
import {
  Mountain,
  Calendar as CalendarIcon,
  MapPin,
  Clock,
  Users,
  Plus,
  ChevronLeft,
  ChevronRight,
  Building2,
  PartyPopper,
  TreePine,
  Utensils,
  Car,
  Trophy,
  Check,
  X,
  Loader2,
  ArrowLeft,
  RefreshCw,
  CalendarDays,
  Bell
} from "lucide-react";
import { format, startOfMonth, endOfMonth, addMonths, subMonths, parseISO, isValid } from "date-fns";

const EventsPage = () => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState("events");
  const [events, setEvents] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [calendarItems, setCalendarItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Dialogs
  const [showRsvpDialog, setShowRsvpDialog] = useState(false);
  const [showReservationDialog, setShowReservationDialog] = useState(false);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedAmenity, setSelectedAmenity] = useState(null);
  
  // Forms
  const [rsvpForm, setRsvpForm] = useState({ status: "attending", guests: 0, notes: "" });
  const [reservationForm, setReservationForm] = useState({
    amenity_id: "",
    start_datetime: "",
    end_datetime: "",
    purpose: "",
    attendees: 1,
    notes: ""
  });
  const [eventForm, setEventForm] = useState({
    title: "",
    description: "",
    category: "social",
    start_datetime: "",
    end_datetime: "",
    location: "",
    capacity: "",
    rsvp_required: false
  });
  
  const [submitting, setSubmitting] = useState(false);

  const isAdmin = user?.role && ["admin", "board", "property_manager"].includes(user.role);

  useEffect(() => {
    fetchData();
  }, [token]);

  useEffect(() => {
    fetchCalendar();
  }, [currentMonth, token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [eventsRes, amenitiesRes, reservationsRes] = await Promise.all([
        axios.get(`${API}/events`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/events/amenities`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/events/reservations?my_only=true`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setEvents(eventsRes.data.events || []);
      setAmenities(amenitiesRes.data.amenities || []);
      setReservations(reservationsRes.data.reservations || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendar = async () => {
    try {
      const start = startOfMonth(currentMonth);
      const end = endOfMonth(currentMonth);
      const res = await axios.get(`${API}/events/calendar`, {
        params: {
          start_date: start.toISOString(),
          end_date: end.toISOString()
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalendarItems(res.data.items || []);
    } catch (error) {
      console.error("Failed to fetch calendar:", error);
    }
  };

  const handleRsvp = async () => {
    if (!selectedEvent) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/events/${selectedEvent.event_id}/rsvp`, null, {
        params: rsvpForm,
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowRsvpDialog(false);
      fetchData();
    } catch (error) {
      console.error("RSVP failed:", error);
      alert(error.response?.data?.detail || "Failed to submit RSVP");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateReservation = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/events/reservations`, null, {
        params: {
          ...reservationForm,
          start_datetime: new Date(reservationForm.start_datetime).toISOString(),
          end_datetime: new Date(reservationForm.end_datetime).toISOString()
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowReservationDialog(false);
      setReservationForm({
        amenity_id: "",
        start_datetime: "",
        end_datetime: "",
        purpose: "",
        attendees: 1,
        notes: ""
      });
      fetchData();
      fetchCalendar();
    } catch (error) {
      console.error("Reservation failed:", error);
      alert(error.response?.data?.detail || "Failed to create reservation");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateEvent = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/events`, null, {
        params: {
          ...eventForm,
          start_datetime: new Date(eventForm.start_datetime).toISOString(),
          end_datetime: new Date(eventForm.end_datetime).toISOString(),
          capacity: eventForm.capacity ? parseInt(eventForm.capacity) : null,
          publish: true
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowEventDialog(false);
      setEventForm({
        title: "",
        description: "",
        category: "social",
        start_datetime: "",
        end_datetime: "",
        location: "",
        capacity: "",
        rsvp_required: false
      });
      fetchData();
      fetchCalendar();
    } catch (error) {
      console.error("Event creation failed:", error);
      alert(error.response?.data?.detail || "Failed to create event");
    } finally {
      setSubmitting(false);
    }
  };

  const cancelReservation = async (reservationId) => {
    if (!confirm("Are you sure you want to cancel this reservation?")) return;
    try {
      await axios.delete(`${API}/events/reservations/${reservationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
      fetchCalendar();
    } catch (error) {
      console.error("Cancel failed:", error);
      alert(error.response?.data?.detail || "Failed to cancel reservation");
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit"
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const getCategoryIcon = (category) => {
    const icons = {
      social: <PartyPopper className="w-5 h-5" />,
      meeting: <Users className="w-5 h-5" />,
      holiday: <TreePine className="w-5 h-5" />,
      volunteer: <Users className="w-5 h-5" />,
      sports: <Trophy className="w-5 h-5" />,
      educational: <Building2 className="w-5 h-5" />
    };
    return icons[category] || <CalendarDays className="w-5 h-5" />;
  };

  const getAmenityIcon = (type) => {
    const icons = {
      clubhouse: <Building2 className="w-5 h-5" />,
      pavilion: <TreePine className="w-5 h-5" />,
      bbq_area: <Utensils className="w-5 h-5" />,
      parking: <Car className="w-5 h-5" />,
      tennis_court: <Trophy className="w-5 h-5" />,
      basketball_court: <Trophy className="w-5 h-5" />
    };
    return icons[type] || <Building2 className="w-5 h-5" />;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-amber-100 text-amber-800",
      approved: "bg-green-100 text-green-800",
      denied: "bg-red-100 text-red-800",
      cancelled: "bg-gray-100 text-gray-800",
      attending: "bg-green-100 text-green-800",
      not_attending: "bg-gray-100 text-gray-800",
      maybe: "bg-blue-100 text-blue-800",
      waitlist: "bg-purple-100 text-purple-800"
    };
    return <Badge className={styles[status] || "bg-gray-100"}>{status?.replace(/_/g, " ")}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  const pageContent = (
    <div className={isAdmin ? "p-6 lg:p-8 max-w-7xl mx-auto" : "min-h-screen bg-background"} data-testid="events-page">
      {/* Header - only for non-admin */}
      {!isAdmin && (
        <header className="sticky top-0 z-50 border-b bg-white/95 backdrop-blur">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/owner/dashboard">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary/80 rounded-xl flex items-center justify-center">
                  <CalendarIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="font-serif text-xl font-semibold">Events & Reservations</h1>
                  <p className="text-sm text-muted-foreground">Community calendar and amenity booking</p>
                </div>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Page Header for Admin */}
      {isAdmin && (
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary/80 rounded-xl flex items-center justify-center">
              <CalendarIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-serif text-xl font-semibold">Events & Reservations</h1>
              <p className="text-sm text-muted-foreground">Community calendar and amenity booking</p>
            </div>
          </div>
          <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90">
                <Plus className="w-4 h-4 mr-2" /> Create Event
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create Community Event</DialogTitle>
                <DialogDescription>Add a new event for the community</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Event Title</Label>
                  <Input
                    value={eventForm.title}
                    onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                    placeholder="Summer BBQ"
                  />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={eventForm.category} onValueChange={(v) => setEventForm({ ...eventForm, category: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="social">Social</SelectItem>
                      <SelectItem value="meeting">Meeting</SelectItem>
                      <SelectItem value="holiday">Holiday</SelectItem>
                      <SelectItem value="volunteer">Volunteer</SelectItem>
                          <SelectItem value="sports">Sports</SelectItem>
                          <SelectItem value="educational">Educational</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Start Date & Time</Label>
                        <Input
                          type="datetime-local"
                          value={eventForm.start_datetime}
                          onChange={(e) => setEventForm({ ...eventForm, start_datetime: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>End Date & Time</Label>
                        <Input
                          type="datetime-local"
                          value={eventForm.end_datetime}
                          onChange={(e) => setEventForm({ ...eventForm, end_datetime: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Location</Label>
                      <Input
                        value={eventForm.location}
                        onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                        placeholder="Community Clubhouse"
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={eventForm.description}
                        onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                        placeholder="Event details..."
                        rows={3}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Capacity (optional)</Label>
                        <Input
                          type="number"
                          value={eventForm.capacity}
                          onChange={(e) => setEventForm({ ...eventForm, capacity: e.target.value })}
                          placeholder="50"
                        />
                      </div>
                      <div className="flex items-center gap-2 pt-6">
                        <input
                          type="checkbox"
                          id="rsvp_required"
                          checked={eventForm.rsvp_required}
                          onChange={(e) => setEventForm({ ...eventForm, rsvp_required: e.target.checked })}
                          className="rounded"
                        />
                        <Label htmlFor="rsvp_required">RSVP Required</Label>
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowEventDialog(false)}>Cancel</Button>
                    <Button onClick={handleCreateEvent} disabled={submitting} className="bg-primary hover:bg-primary/90">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Create Event
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}

          {/* Non-admin users header buttons */}
          {!isAdmin && (
            <div className="flex items-center gap-2 px-4 py-2">
              <Dialog open={showReservationDialog} onOpenChange={setShowReservationDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <Plus className="w-4 h-4 mr-2" /> Book Amenity
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Reserve Amenity</DialogTitle>
                    <DialogDescription>Book a community amenity</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Select Amenity</Label>
                      <Select value={reservationForm.amenity_id} onValueChange={(v) => setReservationForm({ ...reservationForm, amenity_id: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose an amenity" />
                        </SelectTrigger>
                        <SelectContent>
                          {amenities.map((a) => (
                            <SelectItem key={a.amenity_id} value={a.amenity_id}>
                              {a.name} {a.hourly_rate > 0 && `($${a.hourly_rate}/hr)`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Start Date & Time</Label>
                      <Input
                        type="datetime-local"
                        value={reservationForm.start_datetime}
                        onChange={(e) => setReservationForm({ ...reservationForm, start_datetime: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>End Date & Time</Label>
                      <Input
                        type="datetime-local"
                        value={reservationForm.end_datetime}
                        onChange={(e) => setReservationForm({ ...reservationForm, end_datetime: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Purpose</Label>
                    <Input
                      value={reservationForm.purpose}
                      onChange={(e) => setReservationForm({ ...reservationForm, purpose: e.target.value })}
                      placeholder="Birthday party, family gathering, etc."
                    />
                  </div>
                  <div>
                    <Label>Number of Attendees</Label>
                    <Input
                      type="number"
                      min="1"
                      value={reservationForm.attendees}
                      onChange={(e) => setReservationForm({ ...reservationForm, attendees: parseInt(e.target.value) || 1 })}
                    />
                  </div>
                  <div>
                    <Label>Notes (optional)</Label>
                    <Textarea
                      value={reservationForm.notes}
                      onChange={(e) => setReservationForm({ ...reservationForm, notes: e.target.value })}
                      placeholder="Any special requests..."
                      rows={2}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowReservationDialog(false)}>Cancel</Button>
                  <Button onClick={handleCreateReservation} disabled={submitting} className="bg-primary hover:bg-primary/90">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Submit Request
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}

      {/* Main Content */}
      <div className={isAdmin ? "" : "max-w-7xl mx-auto px-4 md:px-8 py-8"}>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 bg-white border mb-6">
            <TabsTrigger value="events" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              Events
            </TabsTrigger>
            <TabsTrigger value="amenities" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              Amenities
            </TabsTrigger>
            <TabsTrigger value="my-reservations" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              My Reservations
            </TabsTrigger>
            <TabsTrigger value="calendar" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              Calendar
            </TabsTrigger>
          </TabsList>

          {/* Events Tab */}
          <TabsContent value="events" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {events.length > 0 ? events.map((event) => (
                <Card key={event.event_id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center text-primary">
                          {getCategoryIcon(event.category)}
                        </div>
                        <div>
                          <CardTitle className="text-base">{event.title}</CardTitle>
                          <Badge variant="outline" className="text-xs capitalize">{event.category}</Badge>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{event.description}</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <CalendarIcon className="w-4 h-4" />
                        {formatDateTime(event.start_datetime)}
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="w-4 h-4" />
                        {event.location}
                      </div>
                      {event.capacity && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Users className="w-4 h-4" />
                          {event.rsvp_count || 0} / {event.capacity} attending
                        </div>
                      )}
                    </div>
                    {event.rsvp_required && (
                      <div className="mt-4">
                        {event.my_rsvp ? (
                          <div className="flex items-center justify-between">
                            <span className="text-sm">Your RSVP:</span>
                            {getStatusBadge(event.my_rsvp.status)}
                          </div>
                        ) : (
                          <Button
                            className="w-full bg-primary hover:bg-primary/90"
                            onClick={() => {
                              setSelectedEvent(event);
                              setShowRsvpDialog(true);
                            }}
                          >
                            RSVP Now
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )) : (
                <div className="col-span-full text-center py-12 text-muted-foreground">
                  <CalendarDays className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No upcoming events scheduled.</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Amenities Tab */}
          <TabsContent value="amenities" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {amenities.length > 0 ? amenities.map((amenity) => (
                <Card key={amenity.amenity_id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-[#B87333]/10 rounded-lg flex items-center justify-center text-[#B87333]">
                        {getAmenityIcon(amenity.amenity_type)}
                      </div>
                      <div>
                        <CardTitle className="text-base">{amenity.name}</CardTitle>
                        <Badge variant="outline" className="text-xs capitalize">{amenity.amenity_type?.replace(/_/g, " ")}</Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {amenity.description && (
                      <p className="text-sm text-muted-foreground mb-3">{amenity.description}</p>
                    )}
                    <div className="space-y-2 text-sm">
                      {amenity.capacity && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Users className="w-4 h-4" />
                          Capacity: {amenity.capacity}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <span className="font-medium text-foreground">
                          {amenity.hourly_rate > 0 ? `$${amenity.hourly_rate}/hour` : "Free"}
                        </span>
                      </div>
                      {amenity.requires_approval && (
                        <div className="flex items-center gap-2 text-amber-600">
                          <Bell className="w-4 h-4" />
                          Requires approval
                        </div>
                      )}
                    </div>
                    <Button
                      className="w-full mt-4 bg-primary hover:bg-primary/90"
                      onClick={() => {
                        setReservationForm({ ...reservationForm, amenity_id: amenity.amenity_id });
                        setShowReservationDialog(true);
                      }}
                    >
                      Reserve
                    </Button>
                  </CardContent>
                </Card>
              )) : (
                <div className="col-span-full text-center py-12 text-muted-foreground">
                  <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No amenities available.</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* My Reservations Tab */}
          <TabsContent value="my-reservations" className="space-y-4">
            {reservations.length > 0 ? (
              <div className="space-y-4">
                {reservations.map((res) => (
                  <Card key={res.reservation_id} className="border-0 shadow-sm">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary">
                            {res.amenity && getAmenityIcon(res.amenity.amenity_type)}
                          </div>
                          <div>
                            <h3 className="font-medium">{res.amenity?.name || "Amenity"}</h3>
                            <p className="text-sm text-muted-foreground">{res.purpose}</p>
                            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <CalendarIcon className="w-4 h-4" />
                                {formatDateTime(res.start_datetime)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {res.attendees} attendees
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          {getStatusBadge(res.status)}
                          {res.fee > 0 && (
                            <span className="text-sm font-medium">${res.fee.toFixed(2)}</span>
                          )}
                          {["pending", "approved"].includes(res.status) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => cancelReservation(res.reservation_id)}
                            >
                              <X className="w-4 h-4 mr-1" /> Cancel
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <CalendarDays className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No reservations yet.</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => setShowReservationDialog(true)}
                >
                  <Plus className="w-4 h-4 mr-2" /> Book an Amenity
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar" className="space-y-4">
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">
                    {format(currentMonth, "MMMM yyyy")}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                    <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                      {day}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {Array.from({ length: 42 }, (_, i) => {
                    const start = startOfMonth(currentMonth);
                    const dayOffset = start.getDay();
                    const dayNum = i - dayOffset + 1;
                    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), dayNum);
                    const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
                    const isToday = date.toDateString() === new Date().toDateString();
                    
                    const dayItems = calendarItems.filter((item) => {
                      const itemDate = new Date(item.start);
                      return itemDate.toDateString() === date.toDateString();
                    });
                    
                    return (
                      <div
                        key={i}
                        className={`min-h-[80px] p-1 border rounded-lg ${
                          isCurrentMonth ? "bg-white" : "bg-gray-50 text-muted-foreground"
                        } ${isToday ? "ring-2 ring-primary" : ""}`}
                      >
                        <div className="text-right text-sm font-medium mb-1">
                          {date.getDate()}
                        </div>
                        <div className="space-y-0.5">
                          {dayItems.slice(0, 2).map((item, idx) => (
                            <div
                              key={idx}
                              className="text-xs px-1 py-0.5 rounded truncate"
                              style={{ backgroundColor: item.color + "20", color: item.color }}
                            >
                              {item.title}
                            </div>
                          ))}
                          {dayItems.length > 2 && (
                            <div className="text-xs text-muted-foreground text-center">
                              +{dayItems.length - 2} more
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Legend */}
                <div className="flex items-center gap-4 mt-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#10B981]" />
                    <span>Events</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#3B82F6]" />
                    <span>Reservations</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#8B5CF6]" />
                    <span>Meetings</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* RSVP Dialog */}
      <Dialog open={showRsvpDialog} onOpenChange={setShowRsvpDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>RSVP to {selectedEvent?.title}</DialogTitle>
            <DialogDescription>
              {formatDateTime(selectedEvent?.start_datetime)} at {selectedEvent?.location}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Will you attend?</Label>
              <Select value={rsvpForm.status} onValueChange={(v) => setRsvpForm({ ...rsvpForm, status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="attending">Yes, I&apos;ll be there!</SelectItem>
                  <SelectItem value="maybe">Maybe</SelectItem>
                  <SelectItem value="not_attending">Sorry, can&apos;t make it</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {rsvpForm.status === "attending" && (
              <div>
                <Label>Additional Guests</Label>
                <Input
                  type="number"
                  min="0"
                  value={rsvpForm.guests}
                  onChange={(e) => setRsvpForm({ ...rsvpForm, guests: parseInt(e.target.value) || 0 })}
                />
              </div>
            )}
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={rsvpForm.notes}
                onChange={(e) => setRsvpForm({ ...rsvpForm, notes: e.target.value })}
                placeholder="Dietary restrictions, questions, etc."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRsvpDialog(false)}>Cancel</Button>
            <Button onClick={handleRsvp} disabled={submitting} className="bg-primary hover:bg-primary/90">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit RSVP
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  if (isAdmin) {
    return <AdminSidebar>{pageContent}</AdminSidebar>;
  }
  return pageContent;
};

export default EventsPage;
